import React from 'react';
import { useInterview } from '../../contexts/InterviewContext';
import UserVideo from './UserVideo';
import AIInterviewer from '../layout/AIInterviewer';
import cn from 'classnames';
import SpeakingIndicator from './SpeakingIndicator';
/**
 * VideoDisplayArea - Container for the interview video displays
 * 
 * This component is responsible for:
 * 1. Managing the main focus between AI and user videos
 * 2. Handling picture-in-picture display
 * 3. Managing the video container styling and animations
 */
const VideoDisplayArea: React.FC<{ isMobileView: boolean }> = ({ isMobileView }) => {
  const { 
    showAiAvatar,
    toggleFocus,
    isScreenSharing,
    userSpeaking,
    aiSpeakingRef,
  } = useInterview();
  
  const aiSpeaking = aiSpeakingRef?.current || false;
  
  return (
    <div className={cn(
      "relative rounded-xl overflow-hidden border border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark shadow-sm flex-1 h-full min-h-0",
      {
        // Apply special styling when screen sharing is active
        "flex items-center justify-center": !showAiAvatar && isScreenSharing
      }
    )}>
      {/* Show either AI avatar or user video as main focus with animations */}
      {showAiAvatar ? (
        // AI Interviewer as main display
        <div className="relative w-full h-full">
          <AIInterviewer isSmallView={false} isMobileView={isMobileView} />
          
          {/* AI Speaking Indicator */}
          {aiSpeaking && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
              <SpeakingIndicator type="ai" isSmallView={isMobileView} />
            </div>
          )}
          
          {/* User video (picture-in-picture) */}
          <div 
            className="absolute bottom-4 right-4 w-32 h-24 md:w-48 md:h-36 rounded-lg overflow-hidden border-2 border-white/50 dark:border-black/30 shadow-lg cursor-pointer hover:scale-105 transition-transform duration-300" 
            onClick={toggleFocus}
          >
            <UserVideo isSmallView={true} isUserSpeaking={userSpeaking} />
          </div>
        </div>
      ) : (
        // User Video as main display
        <div className={cn(
          "relative w-full h-full",
          { "flex items-center justify-center": isScreenSharing }
        )}>
          <UserVideo />
          
          {/* User Speaking Indicator */}
          {userSpeaking && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
              <SpeakingIndicator type="user" isSmallView={isMobileView} />
            </div>
          )}
          
          {/* AI Interviewer (picture-in-picture) */}
          <div 
            className="absolute bottom-4 right-4 w-32 h-24 md:w-48 md:h-36 rounded-lg overflow-hidden border-2 border-white/50 dark:border-black/30 shadow-lg cursor-pointer hover:scale-105 transition-transform duration-300" 
            onClick={toggleFocus}
          >
            <AIInterviewer isSmallView={true} isAISpeaking={aiSpeaking} isMobileView={isMobileView} />
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoDisplayArea; 